import type { JobOrderFilter } from '@/application/ports/JobOrderRepository';
import { strings } from '@/shared/strings';

const text = strings.dashboard;

/** How many cases a list widget shows. The screen it is cut from has the rest. */
export const WIDGET_ROWS = 6;

/** The lists of cases a dashboard can carry, each a list some screen already has. */
export type CaseListKey =
  | 'dueToday'
  | 'overdue'
  | 'urgent'
  | 'latestCases'
  | 'readyToShip'
  | 'onHold'
  | 'toBePaid'
  | 'partPaid'
  | 'changedCases'
  | 'released'
  | 'inCeramic';

export interface CaseListRule {
  /** The small word over the title. */
  kicker: string;
  /** What is asked of the server: the screen's own list, narrowed and cut to a few rows. */
  filter: JobOrderFilter;
  /** Where View all goes: the screen the list is cut from, narrowed the same way where it can be. */
  all: string;
  /** What the end of a row says. */
  end: 'due' | 'received' | 'hold' | 'balance' | 'changes';
  /** A row opens the case's payment summary, which is where Accounts works on it, and not the case. */
  money?: boolean;
}

const first = { page: 1, pageSize: WIDGET_ROWS } as const;

/**
 * Each list as its own screen asks for it. The request of 7 October 2026,
 * sections 6 and 7, made Due and Overdue questions the server answers by its
 * one rule, so a list here holds exactly the cases the Orders list does when
 * it is narrowed the same way, and a count beside it counts exactly those.
 *
 * The three money lists ask for a Payments list by name, as that screen does:
 * an accountant who holds only their own screens is answered a list of job
 * orders no other way.
 */
export const CASE_LISTS: Record<CaseListKey, CaseListRule> = {
  dueToday: { kicker: text.kicker.cases, filter: { due: 'today', ...first }, all: '/orders?due=today', end: 'due' },
  overdue: {
    kicker: text.kicker.cases,
    filter: { due: 'overdue', ...first },
    all: '/orders?due=overdue',
    end: 'due',
  },
  urgent: { kicker: text.kicker.cases, filter: { urgent: true, ...first }, all: '/orders?urgent=1', end: 'due' },
  latestCases: {
    kicker: text.kicker.cases,
    filter: { sort: '-createdAt', ...first },
    all: '/orders',
    end: 'received',
  },
  // Paid and finished: the state the dispatch desk sends from.
  readyToShip: { kicker: text.kicker.dispatch, filter: { state: 'paid', ...first }, all: '/shipping', end: 'due' },
  onHold: { kicker: text.kicker.hold, filter: { state: 'held', ...first }, all: '/on-hold', end: 'hold' },
  // Accounts follow the money past the door: a dispatched case still owes.
  toBePaid: {
    kicker: text.kicker.money,
    filter: { money: 'unpaid', includeDispatched: true, ...first },
    all: '/payments',
    end: 'balance',
    money: true,
  },
  partPaid: {
    kicker: text.kicker.money,
    filter: { money: 'part', includeDispatched: true, ...first },
    all: '/payments?list=part',
    end: 'balance',
    money: true,
  },
  changedCases: {
    kicker: text.kicker.money,
    filter: { money: 'changed', includeDispatched: true, ...first },
    all: '/payments?list=changed',
    end: 'changes',
    money: true,
  },
  // Released to ship unpaid, and at Ceramic now: the lab's request of 17 October 2026, items 8 and 14.
  released: {
    kicker: text.kicker.money,
    filter: { money: 'released', includeDispatched: true, ...first },
    all: '/payments?list=released',
    end: 'balance',
    money: true,
  },
  inCeramic: {
    kicker: text.kicker.money,
    filter: { money: 'ceramic', includeDispatched: true, ...first },
    all: '/payments?list=ceramic',
    end: 'balance',
    money: true,
  },
};

/**
 * The cases still being made: the Orders list narrowed to the active, asked
 * for one row, since only its size is read. The lab's request of
 * 17 October 2026, item 3: a case in Ready or Logistics is not active.
 */
export const ACTIVE: JobOrderFilter = { page: 1, pageSize: 1, active: true };
